/**
 * @file
 * Connecting lines between List (checklist) badges.
 *
 * For a list with "Connecting line" on (.checklist--connector, see
 * paragraph--checklist--default.html.twig), draws a line from each item's
 * badge to the next visible item's badge: horizontal when the two badges sit
 * side by side in a row, vertical when they're stacked, and nothing across a
 * row wrap. Measured from the rendered badges rather than worked out in CSS,
 * because items per row and the gap between them change per breakpoint.
 * Lines run edge to edge between badges (never over them) and are redrawn
 * whenever the list resizes. See docs/checklist_ordered_list_plan.md.
 */
(function (Drupal) {

  'use strict';

  // Centres within this many pixels count as lined up.
  const TOLERANCE = 2;
  const THICKNESS = 2;

  // An item's own badge - not one belonging to a list nested inside it.
  function ownBadge(item) {
    const badges = item.querySelectorAll('.icon-background');
    for (const badge of badges) {
      if (badge.closest('li.checklist-item') === item) {
        return badge;
      }
    }
    return null;
  }

  function draw(list) {
    list.querySelectorAll(':scope > .checklist-connector').forEach((line) => line.remove());

    const listBox = list.getBoundingClientRect();
    // Positions are relative to the list's padding box (absolutely
    // positioned children start inside its border).
    const originX = listBox.left + list.clientLeft;
    const originY = listBox.top + list.clientTop;

    const badges = Array.from(list.children)
      .filter((child) => child.matches('li.checklist-item'))
      .map(ownBadge)
      .filter((badge) => badge && badge.getBoundingClientRect().width > 0);

    for (let i = 0; i < badges.length - 1; i++) {
      const a = badges[i].getBoundingClientRect();
      const b = badges[i + 1].getBoundingClientRect();
      const ay = a.top + a.height / 2;
      const by = b.top + b.height / 2;
      const ax = a.left + a.width / 2;
      const bx = b.left + b.width / 2;

      let style = null;
      if (Math.abs(ay - by) <= TOLERANCE && b.left > a.right) {
        style = {
          left: a.right - originX,
          top: ay - originY - THICKNESS / 2,
          width: b.left - a.right,
          height: THICKNESS,
        };
      }
      else if (Math.abs(ax - bx) <= TOLERANCE && b.top > a.bottom) {
        style = {
          left: ax - originX - THICKNESS / 2,
          top: a.bottom - originY,
          width: THICKNESS,
          height: b.top - a.bottom,
        };
      }
      if (!style) {
        // Not lined up: the next item starts a new row.
        continue;
      }

      const line = document.createElement('span');
      line.className = 'checklist-connector';
      line.setAttribute('aria-hidden', 'true');
      Object.keys(style).forEach((key) => {
        line.style[key] = style[key] + 'px';
      });
      list.appendChild(line);
    }
  }

  Drupal.behaviors.layeroneChecklistConnectors = {
    attach: function (context) {
      const root = context.querySelectorAll ? context : document;
      root.querySelectorAll('.checklist--connector').forEach((list) => {
        if (list.dataset.connectorsBound) {
          return;
        }
        list.dataset.connectorsBound = '1';

        // Debounced with a short timer rather than requestAnimationFrame,
        // which browsers pause in tabs that aren't visible - a page opened in
        // a background tab would otherwise show no lines.
        let timer = null;
        const schedule = () => {
          clearTimeout(timer);
          timer = setTimeout(() => draw(list), 50);
        };

        if ('ResizeObserver' in window) {
          // The list resizing covers breakpoint changes and images loading.
          new ResizeObserver(schedule).observe(list);
        }
        window.addEventListener('resize', schedule);
        window.addEventListener('load', schedule);
        schedule();
      });
    },
  };

})(Drupal);
